import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAppSettings, formatPrice } from "@/hooks/useTrialInfo";
import SharedHeader from "@/components/SharedHeader";
import SharedFooter from "@/components/SharedFooter";
import { 
  GraduationCap, ChevronDown, ChevronUp, Users, BookOpen, 
  Phone, Mail, MessageCircle, Search, ArrowLeft,
  CreditCard, Shield, Clock, Globe, HelpCircle, CheckCircle
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";

// Build parentFAQs dynamically with all settings from system
function buildParentFAQs(settings) {
  const { trial, pricing, policies, support } = settings;
  
  return [
    {
      category: "Getting Started",
      icon: BookOpen,
      questions: [
        {
          q: "How do I register my child on Eduzy?",
          a: "Registration is simple! Click on 'Parent' on our login page, then select 'Register Now'. You'll need to provide your email, verify it with an OTP, and then enter your child's details (name, age, grade, school, and subjects). Once complete, your child will receive a unique Student ID starting with 'Eduzy_'."
        },
        {
          q: "What information do I need to register?",
          a: "You'll need: Your name and email address, your child's name, age (10-14 years), grade (5th-8th), curriculum board (CBSE or IB), school name, and preferred subjects. You'll also need to accept our Terms of Service and Privacy Policy, which comply with India's DPDP Act."
        },
        {
          q: "Is parental consent mandatory?",
          a: "Yes, absolutely. As per India's Digital Personal Data Protection Act (DPDP) 2023, verifiable parental consent is required before any child's data can be collected. You must complete the OTP verification process to provide this consent."
        },
        {
          q: "Can I register multiple children?",
          a: `Yes! You can register multiple children under the same parent email. Each child will receive their own unique Student ID and can have different subject selections and subscription plans. You also get a ${pricing.discounts.sibling_percent}% sibling discount!`
        }
      ]
    },
    {
      category: "Subscription & Pricing",
      icon: CreditCard,
      questions: [
        {
          q: "How much does Eduzy cost?",
          a: `We have 3 plans: Starter (${formatPrice(pricing.starter.price_per_subject)}/subject/month) with ${pricing.starter.daily_limit_display}, Plus (${formatPrice(pricing.plus.price_per_subject)}/subject/month) with ${pricing.plus.daily_limit_display} and leaderboard access, and Pro (${formatPrice(pricing.pro.price_per_subject)}/subject/month) with ${pricing.pro.daily_limit_display} learning, practice tests, and personalized study plans.`
        },
        {
          q: "Is there a free trial?",
          a: `Yes! Every new registration gets a ${trial.days}-day free trial with all Pro features (${trial.daily_limit_minutes} min/day limit). No credit card required. Experience practice tests, study plans, and advanced analytics before you decide.`
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept all major payment methods through Razorpay including UPI, credit/debit cards, net banking, and popular wallets like Paytm and PhonePe."
        },
        {
          q: "Can I get a refund?",
          a: `Yes, we offer refunds within ${policies.refund_period_days} days of purchase if you're not satisfied. Refund requests must be submitted to ${support.email}. Please note that refunds are processed to the original payment method within 5-7 business days.`
        },
        {
          q: "How do I cancel my subscription?",
          a: "You can cancel anytime through the Parent Dashboard or by contacting us. Your child will continue to have access until the end of the current billing period. We don't offer partial refunds for unused time after 7 days."
        }
      ]
    },
    {
      category: "Privacy & Security",
      icon: Shield,
      questions: [
        {
          q: "How is my child's data protected?",
          a: "We take data protection very seriously. All data is encrypted in transit (HTTPS/TLS) and at rest. We comply with India's DPDP Act 2023. Only essential data is collected, and you can request deletion anytime. We never sell or share personal data with third parties for marketing."
        },
        {
          q: "What data do you collect from my child?",
          a: "We collect: Name, age, grade, school name, learning progress, assessment scores, and chat interactions for educational purposes. We do NOT collect location data, photos, or any sensitive personal information beyond what's needed for learning."
        },
        {
          q: "How can I delete my child's account?",
          a: "You can request account deletion through the Parent Dashboard or by contacting us. Important: Account deletion is UNRECOVERABLE. All data including learning progress, assessments, and chat history will be permanently deleted. You'll receive an OTP for verification before deletion."
        },
        {
          q: "What is OTP verification and why is it needed?",
          a: "OTP (One-Time Password) verification protects your child's account. We send OTPs to your registered email or WhatsApp for: registration, account modifications, password changes, and deletion requests. Even our admins cannot make changes without your OTP authorization."
        },
        {
          q: "How long do you keep audit logs?",
          a: "We maintain audit logs of all account activities (logins, modifications, deletions) for 180 days as required for DPDP compliance and dispute resolution. After 180 days, logs are automatically purged."
        }
      ]
    },
    {
      category: "Parent Dashboard",
      icon: Users,
      questions: [
        {
          q: "How do I access the Parent Dashboard?",
          a: "Click on 'Parent' on our login page, enter your registered email and password. You'll receive an OTP on your email for 2-factor authentication. Enter the OTP to access your dashboard."
        },
        {
          q: "What can I do on the Parent Dashboard?",
          a: "You can: View and edit each child's profile, track detailed learning progress, manage subscription, view payment history, download data (DPDP compliance), and withdraw consent if needed."
        },
        {
          q: "How do I track my child's progress?",
          a: "The dashboard shows: Total questions answered, correct answer percentage, daily learning time, subject-wise breakdown, and improvement trends over time. You can see both weekly and monthly progress."
        },
        {
          q: "How do I withdraw consent?",
          a: "You can withdraw consent anytime through the Parent Dashboard by clicking 'Withdraw Consent'. This will terminate your child's account and delete all data within 30 days. You can also send 'WITHDRAW CONSENT' via WhatsApp."
        }
      ]
    }
  ];
}

// Static student FAQs (no dynamic content)
const studentFAQs = [
  {
    category: "Getting Started",
    icon: BookOpen,
    questions: [
      {
        q: "How do I start learning on Eduzy?",
        a: "Once your parent registers you, you'll get a Student ID (like Eduzy_ABC123). Save your parent's WhatsApp number where Eduzy messages come. Just send 'Hi' to begin learning anytime!"
      },
      {
        q: "What is my Student ID?",
        a: "Your Student ID is a unique code starting with 'Eduzy_' followed by letters and numbers (like Eduzy_XY1234). Your parent received this when they registered you. Use this to log into the Student Portal."
      },
      {
        q: "How do I log into the Student Portal?",
        a: "Go to our website, click 'Student', and enter your Student ID. You only need to type the part after 'Eduzy_' - the prefix is already filled in. No password needed!"
      },
      {
        q: "What subjects can I learn?",
        a: "We offer: Mathematics, Science, English, Social Studies, and Hindi. Your parent chose your subjects during registration, but they can add more anytime. You can ask questions on any topic!"
      },
      {
        q: "Does Eduzy support CBSE and IB?",
        a: "Yes! Eduzy supports both CBSE and IB (International Baccalaureate) curricula for Grades 5-8. Your parent selects your board when they register you, and it can be changed anytime from the Parent Dashboard. Lessons, questions and examples are then aligned to your chosen board. Please note: our content is AI-generated and aligned to your board to the best of our ability, so occasional variations from the official syllabus may occur — always cross-check important topics with your school textbooks."
      }
    ]
  },
  {
    category: "Learning on WhatsApp",
    icon: MessageCircle,
    questions: [
      {
        q: "How do I start a learning session?",
        a: "Just send 'Hi' or 'Start' on WhatsApp! Our AI tutor will greet you and offer options: Practice (AI picks topics based on your learning gaps), Ask a question, or Request a specific topic. It's that simple!"
      },
      {
        q: "How does the AI choose what to teach?",
        a: "Our AI analyzes your past performance, identifies weak areas, and picks topics you need to practice most. It also follows your grade's curriculum progression. You can always ask for a specific topic if you prefer."
      },
      {
        q: "Can I ask any question?",
        a: "Yes! You can ask questions on any topic at any time. Just type your question naturally like 'What is photosynthesis?' or 'How do I solve quadratic equations?' The AI will answer and offer follow-up practice."
      },
      {
        q: "How do I end a session?",
        a: "Send 'END SESSION' or 'Bye' when you're done. This saves your progress and lets you continue later from where you left off."
      },
      {
        q: "Can I change the language?",
        a: "Yes! Send 'CHANGE LANGUAGE' and choose from: English, Hindi, Punjabi, Tamil, Marathi, Telugu, or Kannada. Your tutor will then teach you in your preferred language."
      },
      {
        q: "What if I don't understand something?",
        a: "Just ask! Type your question or doubt, and the AI tutor will explain it differently. You can ask 'explain again', 'give me an example', or 'make it simpler' - the tutor adapts to help you understand."
      },
      {
        q: "How do I check my progress?",
        a: "Send 'MY PROGRESS' to see your stats including questions answered, accuracy rate, and topics covered. You can also see detailed stats on the Student Portal."
      },
      {
        q: "What commands can I use?",
        a: "Main commands: START, END SESSION, CHANGE LANGUAGE, MY PROGRESS, MY SUBSCRIPTION, HELP. But honestly, just chat naturally! Ask questions, request topics, or let the AI guide you."
      }
    ]
  },
  {
    category: "Student Portal",
    icon: GraduationCap,
    questions: [
      {
        q: "What can I see on my Student Portal?",
        a: "Your profile shows: Your name, grade, school, subjects, total questions answered, correct answers, accuracy percentage, and your learning streak. It's like your personal report card!"
      },
      {
        q: "Why should I use the Student Portal?",
        a: "The portal shows your complete learning journey! See how many questions you've answered, track your accuracy improving over time, and feel proud of your progress."
      },
      {
        q: "Can I change my password?",
        a: "The Student Portal doesn't require a password anymore! Just enter your Student ID to access your dashboard. This makes it super easy to check your progress."
      }
    ]
  },
  {
    category: "Daily Limits & Subscription",
    icon: Clock,
    questions: [
      {
        q: "How long can I learn each day?",
        a: "It depends on your plan: Free Trial gets 4 hours/day with all features, Starter gets 1 hour/day, Plus gets 2 hours/day, and Pro gets 4 hours/day. This ensures quality learning while maintaining healthy screen time."
      },
      {
        q: "How many practice tests can I take?",
        a: "Practice tests vary by plan: Starter allows 1 test per day, Plus allows 3 tests per day, and Pro has unlimited practice tests. During the free trial, you get unlimited tests to experience the full learning experience!"
      },
      {
        q: "What happens when my time is up?",
        a: "You'll receive a friendly message saying your session limit is reached. Don't worry - your progress is saved! Come back tomorrow to continue learning."
      },
      {
        q: "How do I check my subscription?",
        a: "Send 'MY SUBSCRIPTION' on WhatsApp to see your plan details, remaining trial days (if applicable), daily time limits, and practice test quota."
      }
    ]
  }
];

// FAQ Item Component
function FAQItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-medium text-gray-900 pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-emerald-600 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 text-gray-600 leading-relaxed border-t border-gray-100 pt-3 bg-gray-50">
          {answer}
        </div>
      )}
    </div>
  );
}

// FAQ Category Component
function FAQCategory({ category, icon: Icon, questions, openItems, toggleItem, prefix }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
          <Icon className="h-5 w-5 text-emerald-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900">{category}</h3>
      </div>
      <div className="space-y-3">
        {questions.map((item, idx) => {
          const key = `${prefix}-${category}-${idx}`;
          return (
            <FAQItem
              key={key}
              question={item.q}
              answer={item.a}
              isOpen={openItems.includes(key)}
              onClick={() => toggleItem(key)}
            />
          );
        })}
      </div>
    </div>
  );
}

// Contact Form Component
function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setLoading(true);
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success("Your message has been sent! We'll get back to you within 24 hours.");
    setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Your name"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="your@email.com"
            required
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone (Optional)</label>
          <Input
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+91 XXXXX XXXXX"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
          <Input
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            placeholder="What's this about?"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
        <Textarea
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          placeholder="How can we help you?"
          rows={4}
          required
        />
      </div>
      <Button 
        type="submit" 
        disabled={loading}
        className="w-full bg-emerald-600 hover:bg-emerald-700"
      >
        {loading ? "Sending..." : "Send Message"}
      </Button>
    </form>
  );
}

function FAQBody() {
  const { settings } = useAppSettings();
  const [activeTab, setActiveTab] = useState("parents");
  const [openItems, setOpenItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Build parentFAQs with all dynamic settings
  const parentFAQs = buildParentFAQs(settings);

  const toggleItem = (key) => {
    setOpenItems(prev => 
      prev.includes(key) 
        ? prev.filter(item => item !== key)
        : [...prev, key]
    );
  };

  // Filter FAQs based on search
  const filterFAQs = (faqs, prefix) => {
    if (!searchQuery.trim()) return faqs;
    
    const query = searchQuery.toLowerCase();
    return faqs.map(category => ({
      ...category,
      questions: category.questions.filter(
        item => item.q.toLowerCase().includes(query) || item.a.toLowerCase().includes(query)
      )
    })).filter(category => category.questions.length > 0);
  };

  const filteredParentFAQs = filterFAQs(parentFAQs, "parent");
  const filteredStudentFAQs = filterFAQs(studentFAQs, "student");

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 py-4 px-6">
        <SharedHeader 
          transparent={true}
          rightContent={
            <Link to="/">
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          }
        />
      </header>

      {/* Hero Section */}
      <section className="bg-emerald-600 text-white py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-emerald-100 mb-8">
            Find answers to common questions about Eduzy. Can't find what you're looking for? Contact us!
          </p>
          
          {/* Search Bar */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search FAQs..."
              className="pl-12 py-3 text-gray-900 bg-white border-0"
            />
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab("parents")}
              className={`py-4 px-6 font-medium border-b-2 transition-colors ${
                activeTab === "parents"
                  ? "border-emerald-600 text-emerald-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <Users className="h-4 w-4 inline-block mr-2" />
              For Parents
            </button>
            <button
              onClick={() => setActiveTab("students")}
              className={`py-4 px-6 font-medium border-b-2 transition-colors ${
                activeTab === "students"
                  ? "border-emerald-600 text-emerald-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <GraduationCap className="h-4 w-4 inline-block mr-2" />
              For Students
            </button>
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Parent FAQs */}
        {activeTab === "parents" && (
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">FAQs for Parents</h2>
              <p className="text-gray-600">
                Everything you need to know about registering, managing, and monitoring your child's learning journey.
              </p>
            </div>
            
            {filteredParentFAQs.length > 0 ? (
              filteredParentFAQs.map((category) => (
                <FAQCategory
                  key={category.category}
                  {...category}
                  openItems={openItems}
                  toggleItem={toggleItem}
                  prefix="parent"
                />
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No FAQs found matching "{searchQuery}"</p>
              </div>
            )}
          </div>
        )}

        {/* Student FAQs */}
        {activeTab === "students" && (
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">FAQs for Students</h2>
              <p className="text-gray-600">
                Learn how to use Eduzy, chat with your AI tutor, and make the most of your learning experience.
              </p>
            </div>
            
            {filteredStudentFAQs.length > 0 ? (
              filteredStudentFAQs.map((category) => (
                <FAQCategory
                  key={category.category}
                  {...category}
                  openItems={openItems}
                  toggleItem={toggleItem}
                  prefix="student"
                />
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No FAQs found matching "{searchQuery}"</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Contact Section */}
      <section className="bg-white border-t border-gray-200 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Still Have Questions?</h2>
            <p className="text-gray-600">
              Can't find what you're looking for? Our support team is here to help!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Email Card */}
            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Email Us</h3>
              <a 
                href="mailto:info@contentiumtech.com" 
                className="text-emerald-600 hover:underline"
              >
                info@contentiumtech.com
              </a>
              <p className="text-sm text-gray-500 mt-2">We respond within 24 hours</p>
            </div>

            {/* Phone Card */}
            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Call Us</h3>
              <a 
                href="tel:+919654006470" 
                className="text-emerald-600 hover:underline"
              >
                +91 9654006470
              </a>
              <p className="text-sm text-gray-500 mt-2">Mon-Sat, 9 AM - 6 PM IST</p>
            </div>

            {/* WhatsApp Card */}
            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">WhatsApp</h3>
              <p className="text-gray-600">Send "HELP" to our number</p>
              <p className="text-sm text-gray-500 mt-2">Get instant assistance</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gray-50 rounded-xl p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">Send Us a Message</h3>
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Footer */}
      <SharedFooter />
    </div>
  );
}

// Export the FAQ component
export default function FAQ() {
  return <FAQBody />;
}
